import { useState } from 'react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    width: '100%',
    maxWidth: '380px',
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '28px 24px',
    boxShadow: 'var(--shadow-lg)',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
    color: 'var(--text-primary)',
  },
  warning: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: 'var(--text-secondary)',
    marginBottom: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px',
    padding: '16px',
    background: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-md)',
  },
  formTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    marginBottom: '4px',
  },
  input: {
    fontSize: '14px',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  successBox: {
    padding: '16px',
    background: 'rgba(34, 197, 94, 0.08)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '16px',
  },
  successTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--success)',
    marginBottom: '8px',
  },
  successDetail: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    lineHeight: 1.4,
  },
};

export default function LogoutModal({ onClose, onLogout }) {
  const { updateUser } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const handleSetCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const res = await api.put('/auth/set-credentials', { email, password });
      updateUser(res.data.user);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.title}>
          {saved ? 'Credentials Saved!' : 'Log out of test account?'}
        </h3>

        {saved ? (
          <>
            <div style={styles.successBox}>
              <div style={styles.successTitle}>✓ Account saved</div>
              <div style={styles.successDetail}>
                You can now log back in with:<br />
                <strong>{email}</strong>
              </div>
            </div>
            <div style={styles.actions}>
              <button
                className="btn btn-primary btn-full"
                onClick={onClose}
                style={{ padding: '12px' }}
              >
                Continue using app
              </button>
              <button
                className="btn btn-secondary btn-full"
                onClick={onLogout}
                style={{ padding: '12px' }}
              >
                Log out now
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={styles.warning}>
              This is a randomly generated test account. If you log out now,
              you won't be able to access this profile again unless you set
              a username and password first.
            </p>

            {showForm ? (
              <form style={styles.form} onSubmit={handleSetCredentials}>
                <div style={styles.formTitle}>Set login credentials</div>
                <div className="form-group">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    required
                    style={styles.input}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password (8+ characters)"
                    required
                    minLength={8}
                    style={styles.input}
                  />
                </div>
                {error && <p className="error-text">{error}</p>}
                <button
                  type="submit"
                  className="btn btn-primary btn-full"
                  disabled={saving}
                  style={{ padding: '10px', fontSize: '13px' }}
                >
                  {saving ? 'Saving...' : 'Save credentials'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-full"
                  onClick={() => setShowForm(false)}
                  style={{ padding: '8px', fontSize: '13px' }}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <div style={styles.actions}>
                <button
                  className="btn btn-primary btn-full"
                  onClick={() => setShowForm(true)}
                  style={{ padding: '12px' }}
                >
                  Set username & password
                </button>
                <button
                  className="btn btn-secondary btn-full"
                  onClick={onLogout}
                  style={{ padding: '12px', color: 'var(--danger)' }}
                >
                  Log out anyway
                </button>
                <button
                  className="btn btn-ghost btn-full"
                  onClick={onClose}
                  style={{ padding: '10px', fontSize: '13px' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
